/**
 * Render a cropped + compressed JPEG/WebP data URI from a source image.
 *
 *   getCroppedImg(srcDataUri, croppedAreaPixels, { maxWidth, mime, quality })
 *
 * `croppedAreaPixels` is the `{x, y, width, height}` object react-easy-crop
 * provides via its `onCropComplete` callback. We render that rect onto an
 * off-screen canvas (capped at `maxWidth` so payloads stay small), then
 * spit out a data URI ready to PUT to /api/restaurants/.../items.
 *
 * The renderer fills with `#ffffff` first — JPEG has no alpha so PNG /
 * WebP-with-transparency uploads don't ghost dark on the dashboard.
 */
export async function getCroppedImg(srcDataUri, area, opts = {}) {
  const { maxWidth = 1600, mime = 'image/jpeg', quality = 0.86 } = opts;

  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = srcDataUri;
  });

  // Down-scale the captured crop region if it's wider than `maxWidth`,
  // preserving aspect ratio.
  let outW = area.width;
  let outH = area.height;
  if (outW > maxWidth) {
    outH = Math.round((outH * maxWidth) / outW);
    outW = maxWidth;
  }

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  // White matte under transparent PNGs — see header.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, outW, outH);
  return canvas.toDataURL(mime, quality);
}
